/**
 * assets/nalli-delivery-estimate.js — <nalli-delivery-estimate>
 * Pairs with blocks/nalli-delivery-estimate.liquid (Nalli custom theme block).
 *
 * Location-aware "estimated delivery date" checker backed by the Nalli ERP EDD API:
 *   GET {data-endpoint}/{pincode_or_city}/{data-branch}   header: apikey
 *   -> { "min": 2, "max": 2 }  (days; max <= 0 => not serviceable)
 *
 * Country (no dropdown needed), in this order:
 *   a. the currency the shopper picked in the header switcher (assets/nalli-currency.js,
 *      localStorage nalli_display_currency) when it belongs to one country (USD -> US,
 *      GBP -> GB, INR -> IN ...). EUR / AUTO say nothing about the country.
 *   b. Shopify's localization country (data-market-country) when it is not India
 *   c. the shopper's real IP country: nalli-currency's cached Cloudflare lookup
 *      (nalli_real_country), else Shopify /browsing_context_suggestions.json
 *   d. India
 *   A remembered pincode is only restored when it was saved for that country, and
 *   geolocation (auto or "Use my location") can still move it to where the shopper is.
 *   Switching currency re-runs the detection.
 * The active country is shown in [data-edd-country-note] ("Delivering to [country]").
 *
 * Shopper location sources:
 *   1. remembered { country, value } from a previous check: sessionStorage (always,
 *      with the API results, so other pages render instantly) then localStorage (data-remember)
 *   2. browser geolocation (navigator.geolocation) -> reverse geocode -> country + pincode + city
 *        provider 'bigdatacloud' : keyless, country + city only (no postcode data)
 *        provider 'google'       : Geocoding API key required, also returns the postcode
 *   3. IP country from Shopify's own /browsing_context_suggestions.json (no prompt, country only)
 *   4. the market country rendered by Liquid (data-country) + manual entry
 *
 * International fallback: the ERP only knows Indian pincodes (non-IN input returns
 * { min: 0, max: 0 }, and a foreign postcode equal to an Indian pincode would get an
 * Indian answer), so for any country other than IN the API is not called and
 * data-intl-min..data-intl-max business days (Mon-Fri, default 5-8) are shown. Set both
 * to 0 to call the API for international input instead.
 *
 * Messages only (data-show-dates != 'true', the default since 2026-10-16): no ERP call,
 * a valid entry shows data-text-domestic (India) or data-text-international and fires
 * the GA4 event `pincode_collect` { pincode | zipcode | city, country } through
 * assets/nalli-datalayer.js (window.nalliDL.emit). Fired only for shopper actions
 * (Check / "Use my location"), never for the remembered entry restored on page load.
 *
 * India range (data-show-dates='true'): the ERP SLA (max days) is the earliest date and data-range-extra days
 * (default 2) after it the latest: "between Sat, 17 Oct to Mon, 19 Oct".
 *
 * Modes (picked per country, see NO_POSTAL_CODE):
 *   pincode  India, strict 6-digit PIN
 *   postcode any other country with postal codes (generic validation)
 *   city     countries without postal codes: the city name is sent instead
 *
 * {branch} is NOT the shopper: it is the variant's warehouse "Location" option,
 * rendered server-side as data-branch and re-read on variant change
 * (`shopify:product:select` on Horizon 4.x, `variant:update` on Horizon 2.x).
 *
 * Plain ES module, no importmap dependencies, so the same file runs on both
 * Nalli theme lineages (Horizon 4.x v2.0.0 and the Horizon 2.x v1.1.x fork).
 */

const STORAGE_KEY = 'nalli_edd';
const LEGACY_STORAGE_KEY = 'nalli_edd_pincode';
const SESSION_KEY = 'nalli_edd_session';
const SESSION_CACHE_KEY = 'nalli_edd_results';
const GEO_DENIED_KEY = 'nalli_edd_geo_denied';
const CURRENCY_KEY = 'nalli_display_currency';
const REAL_COUNTRY_KEY = 'nalli_real_country';
const REAL_COUNTRY_TTL_MS = 24 * 60 * 60 * 1000;
const TIMEOUT_MS = 10000;
const GEO_TIMEOUT_MS = 8000;
const IST = 'Asia/Kolkata';
const DAY_MS = 86400000;

/** Countries with no postal-code system (Shopify country-service, 2026-08). Keep in sync with the Liquid. */
const NO_POSTAL_CODE = new Set(
  'AC AE AG AI AO AW BI BJ BO BQ BS BW BZ CC CD CF CG CI CK CM CW CX DJ DM ER FJ FK GA GD GI GM GQ GS GY HK JM KM ML MO MR NF NR NU PN QA RW SB SC SH SL SR SS ST SX TA TC TD TG TK TL TO TV UG VA VU YE ZW'.split(
    ' '
  )
);

const VALIDATORS = {
  pincode: /^[1-9][0-9]{5}$/,
  postcode: /^[A-Za-z0-9][A-Za-z0-9 -]{1,9}$/,
  city: /^[\p{L}][\p{L} .'’-]{1,59}$/u,
};

const INPUT_ATTRS = {
  pincode: { name: 'pincode', inputmode: 'numeric', maxlength: '6', pattern: '[1-9][0-9]{5}', autocomplete: 'postal-code' },
  postcode: { name: 'pincode', inputmode: 'text', maxlength: '10', pattern: null, autocomplete: 'postal-code' },
  city: { name: 'city', inputmode: 'text', maxlength: '60', pattern: null, autocomplete: 'address-level2' },
};

/**
 * API results for this browser session ({country}|{value}|{branch} -> {min,max}), so a
 * pincode checked once renders instantly on every other product page without a refetch.
 * Only real API payloads are stored, never errors or the international fallback.
 */
const cache = {
  /** @type {Map<string, {min:number,max:number}>} */
  memory: new Map(),
  get(key) {
    if (this.memory.has(key)) return this.memory.get(key);
    try {
      const hit = JSON.parse(window.sessionStorage.getItem(SESSION_CACHE_KEY) || '{}')[key];
      if (hit && Number.isFinite(hit.min) && Number.isFinite(hit.max)) {
        this.memory.set(key, hit);
        return hit;
      }
    } catch {
      /* ignore */
    }
    return undefined;
  },
  set(key, value) {
    this.memory.set(key, value);
    try {
      const all = JSON.parse(window.sessionStorage.getItem(SESSION_CACHE_KEY) || '{}');
      all[key] = value;
      window.sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(all));
    } catch {
      /* private mode / quota — ignore */
    }
  },
};
/**
 * Currencies that identify a single destination country. Mirrors COUNTRY_CURRENCY in
 * assets/nalli-currency.js; EUR is shared by many countries so it is deliberately absent.
 */
const CURRENCY_COUNTRY = {
  INR: 'IN', USD: 'US', GBP: 'GB', AED: 'AE', SGD: 'SG', AUD: 'AU', CAD: 'CA', NZD: 'NZ',
  MYR: 'MY', LKR: 'LK', NPR: 'NP', ZAR: 'ZA', JPY: 'JP', CNY: 'CN', HKD: 'HK', CHF: 'CH',
  SEK: 'SE', NOK: 'NO', DKK: 'DK', QAR: 'QA', KWD: 'KW', BHD: 'BH', OMR: 'OM', SAR: 'SA',
  THB: 'TH', IDR: 'ID', PHP: 'PH', KRW: 'KR', BDT: 'BD', PKR: 'PK', MUR: 'MU', FJD: 'FJ',
  ILS: 'IL', TRY: 'TR', RUB: 'RU', BRL: 'BR', MXN: 'MX', ARS: 'AR', PLN: 'PL', CZK: 'CZ',
  HUF: 'HU', RON: 'RO',
};

/** Country implied by the header currency switcher, or '' (AUTO / EUR / unknown). */
function countryFromCurrency() {
  try {
    const choice = String(window.localStorage.getItem(CURRENCY_KEY) || '').toUpperCase();
    return CURRENCY_COUNTRY[choice] || '';
  } catch {
    return '';
  }
}

/** @type {Promise<string> | null} one IP lookup per page, shared by all instances */
let ipCountryPromise = null;

const storage = {
  /**
   * Last checked entry: this session's first (always kept), then the device-level one.
   * @param {boolean} remember include the localStorage (device) entry
   * @returns {{country?: string, value?: string} | null}
   */
  get(remember = true) {
    try {
      const session = window.sessionStorage.getItem(SESSION_KEY);
      if (session) return JSON.parse(session);
      if (!remember) return null;
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
      const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
      return legacy ? { country: 'IN', value: legacy } : null;
    } catch {
      return null;
    }
  },
  set(country, value, remember = true) {
    try {
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ country, value }));
      if (remember) window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ country, value }));
    } catch {
      /* private mode / quota — ignore */
    }
  },
  flag(key, on) {
    try {
      if (on) window.localStorage.setItem(key, '1');
      else window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
  has(key) {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  },
};

/**
 * @param {string} countryCode ISO 3166-1 alpha-2
 * @returns {'pincode' | 'postcode' | 'city'}
 */
export function modeForCountry(countryCode) {
  const code = String(countryCode || '').toUpperCase();
  if (code === 'IN') return 'pincode';
  if (NO_POSTAL_CODE.has(code)) return 'city';
  return 'postcode';
}

/**
 * Replace [token] placeholders in a message template.
 * @param {string} template
 * @param {Record<string, string>} vars
 */
const fill = (template, vars) =>
  String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in vars ? vars[key] : match));

/** @param {Date} date */
const weekdayIST = (date) => new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: IST }).format(date);

/**
 * @param {Date} date
 * @param {string} style short | weekday | long
 */
function formatDate(date, style) {
  const options =
    style === 'short'
      ? { day: 'numeric', month: 'short' }
      : style === 'long'
        ? { weekday: 'long', day: 'numeric', month: 'long' }
        : { weekday: 'short', day: 'numeric', month: 'short' };
  return new Intl.DateTimeFormat('en-IN', { ...options, timeZone: IST }).format(date);
}

/**
 * Today + `days`, not counting (and never landing on) the weekdays in `skip`.
 * @param {number} days
 * @param {Set<string>} skip short IST weekday names, e.g. Sun
 */
function addDays(days, skip) {
  const date = new Date();
  let remaining = Math.max(0, days);
  while (remaining > 0) {
    date.setTime(date.getTime() + DAY_MS);
    if (skip.has(weekdayIST(date))) continue;
    remaining -= 1;
  }
  while (skip.has(weekdayIST(date))) date.setTime(date.getTime() + DAY_MS);
  return date;
}

/**
 * Normalise user input for a mode: pincodes keep digits only, postcodes are
 * upper-cased and squeezed, cities are trimmed with collapsed spaces.
 * @param {string} raw
 * @param {string} mode
 */
function normalise(raw, mode) {
  const value = String(raw || '');
  if (mode === 'pincode') return value.replace(/\D/g, '').slice(0, 6);
  if (mode === 'postcode') return value.toUpperCase().replace(/\s+/g, ' ').trim().slice(0, 10);
  return value.replace(/\s+/g, ' ').trim().slice(0, 60);
}

/** GA4 param per input mode: India pincode, other postal codes, city for no-postcode countries. */
const TRACK_PARAM = { pincode: 'pincode', postcode: 'zipcode', city: 'city' };

/**
 * Send a GA4 event through assets/nalli-datalayer.js, which inits on idle: retry briefly
 * when the shopper acts before it is ready.
 * @param {string} name
 * @param {Record<string, string>} params
 * @param {number} [tries]
 */
function track(name, params, tries = 20) {
  if (window.nalliDL?.emit) window.nalliDL.emit(name, params);
  else if (tries > 0) window.setTimeout(() => track(name, params, tries - 1), 250);
}

/** Shopify's IP-based country suggestion (same origin, no prompt). Resolves '' when unavailable. */
function detectCountryByIP() {
  // nalli-currency.js already resolved the real country (Cloudflare trace) for AUTO currency
  try {
    const cached = JSON.parse(window.localStorage.getItem(REAL_COUNTRY_KEY) || 'null');
    if (cached?.country && Date.now() - cached.ts < REAL_COUNTRY_TTL_MS) {
      return Promise.resolve(String(cached.country).toUpperCase());
    }
  } catch {
    /* ignore */
  }
  if (!ipCountryPromise) {
    ipCountryPromise = fetch('/browsing_context_suggestions.json?country%5Benabled%5D=true', {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => String(data?.detected_values?.country?.handle || '').toUpperCase())
      .catch(() => '');
  }
  return ipCountryPromise;
}

/**
 * Reverse geocode lat/lng into { country, postcode, city }.
 * @param {'bigdatacloud' | 'google'} provider
 * @param {string} apiKey
 * @param {number} latitude
 * @param {number} longitude
 * @param {AbortSignal} signal
 */
async function reverseGeocode(provider, apiKey, latitude, longitude, signal) {
  if (provider === 'google' && apiKey) {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${encodeURIComponent(apiKey)}`;
    const data = await fetch(url, { signal }).then((r) => r.json());
    if (data?.status !== 'OK' || !data.results?.length) throw new Error(`geocode ${data?.status || 'empty'}`);
    const out = { country: '', postcode: '', city: '' };
    const pick = (result, type) => result.address_components?.find((c) => c.types?.includes(type));
    for (const result of data.results) {
      if (!out.country) out.country = pick(result, 'country')?.short_name || '';
      if (!out.postcode) out.postcode = pick(result, 'postal_code')?.long_name || '';
      if (!out.city) {
        out.city =
          pick(result, 'locality')?.long_name ||
          pick(result, 'postal_town')?.long_name ||
          pick(result, 'administrative_area_level_2')?.long_name ||
          '';
      }
      if (out.country && out.postcode && out.city) break;
    }
    return out;
  }

  const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;
  const data = await fetch(url, { signal }).then((r) => r.json());
  return {
    country: String(data?.countryCode || '').toUpperCase(),
    postcode: String(data?.postcode || ''),
    city: String(data?.city || data?.locality || ''),
  };
}

class NalliDeliveryEstimate extends HTMLElement {
  static get observedAttributes() {
    return ['data-branch'];
  }

  /** @type {AbortController | null} */
  #abort = null;
  /** @type {AbortController | null} */
  #geoAbort = null;
  #value = '';
  #connected = false;

  connectedCallback() {
    this.form = this.querySelector('[data-edd-form]');
    this.input = this.querySelector('[data-edd-input]');
    this.inputLabel = this.querySelector('[data-edd-input-label]');
    this.button = this.querySelector('[data-edd-button]');
    this.geoButton = this.querySelector('[data-edd-geo]');
    this.result = this.querySelector('[data-edd-result]');
    this.countrySelect = this.querySelector('[data-edd-country]');
    this.countryNote = this.querySelector('[data-edd-country-note]');
    this.marketCountry = String(this.dataset.marketCountry || this.dataset.country || 'IN').toUpperCase();
    if (!this.form || !this.input || !this.result) return;

    this.form.addEventListener('submit', this.#onSubmit);
    this.input.addEventListener('input', this.#onInput);
    this.countrySelect?.addEventListener('change', this.#onCountryChange);
    this.geoButton?.addEventListener('click', this.#onGeoClick);

    // Variant change: Horizon 4.x (bubbling, carries a promise) and Horizon 2.x.
    this.scope = this.closest('.shopify-section, dialog') || document;
    this.scope.addEventListener('shopify:product:select', this.#onProductSelect);
    this.scope.addEventListener('variant:update', this.#onVariantUpdate);
    document.addEventListener('nalli:edd:change', this.#onSiblingChange);

    this.#connected = true;
    this.#applyMode(modeForCountry(this.country));
    this.#updateCountryNote();
    if (!('geolocation' in navigator)) this.geoButton?.setAttribute('hidden', '');

    // The header currency switcher sets <html data-nalli-currency> after every change
    this.currencyObserver = new MutationObserver(() => this.#retarget());
    this.currencyObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-nalli-currency'] });

    this.#start();
  }

  disconnectedCallback() {
    this.#connected = false;
    this.currencyObserver?.disconnect();
    this.#abort?.abort();
    this.#geoAbort?.abort();
    this.form?.removeEventListener('submit', this.#onSubmit);
    this.input?.removeEventListener('input', this.#onInput);
    this.countrySelect?.removeEventListener('change', this.#onCountryChange);
    this.geoButton?.removeEventListener('click', this.#onGeoClick);
    this.scope?.removeEventListener('shopify:product:select', this.#onProductSelect);
    this.scope?.removeEventListener('variant:update', this.#onVariantUpdate);
    document.removeEventListener('nalli:edd:change', this.#onSiblingChange);
  }

  /** Re-check when the branch changes under us (event handler or a DOM morph). */
  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.#connected || name !== 'data-branch' || oldValue === newValue) return;
    if (this.#value) this.check(this.#value, { silent: true });
  }

  get remember() {
    return this.dataset.remember !== 'false';
  }

  get branch() {
    return (this.dataset.branch || '').trim() || 'ESHOP';
  }

  get endpoint() {
    return (this.dataset.endpoint || '').trim().replace(/\/+$/, '');
  }

  /** ISO code of the country being checked: inline dropdown first, else the detected/market country. */
  get country() {
    const selected = this.countrySelect?.value;
    return (selected || this.dataset.country || 'IN').toUpperCase();
  }

  get countryName() {
    const option = this.countrySelect?.selectedOptions?.[0];
    if (option?.textContent?.trim()) return option.textContent.trim();
    if (this.dataset.countryName) return this.dataset.countryName;
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(this.country) || this.country;
    } catch {
      return this.country;
    }
  }

  /** Default day range for non-India destinations, or null when disabled / not international. */
  get intlFallback() {
    if (this.country === 'IN') return null;
    const min = Number.parseInt(this.dataset.intlMin ?? '', 10);
    const max = Number.parseInt(this.dataset.intlMax ?? '', 10);
    if (!Number.isFinite(min) || !Number.isFinite(max) || Math.max(min, max) <= 0) return null;
    return { min: Math.min(min, max), max: Math.max(min, max) };
  }

  get mode() {
    return this.dataset.mode || 'pincode';
  }

  /** Startup: see "Country" in the header comment. */
  async #start() {
    const country = await this.#resolveCountry();
    if (!this.#connected || this.#value) return;
    if (this.#restore([country])) return;
    this.#setCountry(country);

    const autoGeo = this.dataset.geoAuto === 'true' && !storage.has(GEO_DENIED_KEY);
    if (autoGeo && !this.#value) this.locate({ auto: true });
  }

  /** @returns {Promise<string>} ISO code of the destination to check (see header comment). */
  async #resolveCountry() {
    const fromCurrency = countryFromCurrency();
    if (fromCurrency) return fromCurrency;
    if (this.marketCountry && this.marketCountry !== 'IN') return this.marketCountry;
    return (await detectCountryByIP()) || this.marketCountry || 'IN';
  }

  /** Currency switched in the header: move to the country it implies. */
  async #retarget() {
    const country = await this.#resolveCountry();
    if (!this.#connected || !this.input || country === this.country) return;
    this.#abort?.abort();
    this.input.value = '';
    this.#value = '';
    this.#render('', '');
    if (this.#restore([country])) return;
    this.#setCountry(country);
  }

  /**
   * Ask the browser for its position, reverse geocode it and run the check.
   * @param {{ auto?: boolean }} [options] auto = started without a click (stay silent on failure)
   */
  locate({ auto = false } = {}) {
    if (!('geolocation' in navigator)) {
      if (!auto) this.#render('error', fill(this.dataset.textGeoFailed, this.#vars('')));
      return;
    }

    this.#geoAbort?.abort();
    const controller = new AbortController();
    this.#geoAbort = controller;
    this.toggleAttribute('data-locating', true);
    this.geoButton?.setAttribute('aria-busy', 'true');

    const done = () => {
      if (this.#geoAbort === controller) this.#geoAbort = null;
      this.toggleAttribute('data-locating', false);
      this.geoButton?.setAttribute('aria-busy', 'false');
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const place = await reverseGeocode(
            this.dataset.geoProvider === 'google' ? 'google' : 'bigdatacloud',
            this.dataset.geoKey || '',
            latitude,
            longitude,
            controller.signal
          );
          if (controller.signal.aborted || !this.#connected) return;
          storage.flag(GEO_DENIED_KEY, false);
          this.#applyDetected(place, auto);
        } catch (error) {
          if (controller.signal.aborted) return;
          console.warn('[nalli-delivery-estimate] reverse geocode failed:', error);
          if (!auto) this.#render('error', fill(this.dataset.textGeoFailed, this.#vars('')));
        } finally {
          done();
        }
      },
      (error) => {
        done();
        if (error?.code === 1) storage.flag(GEO_DENIED_KEY, true); // PERMISSION_DENIED: do not auto-prompt again
        if (!auto) this.#render('error', fill(this.dataset.textGeoFailed, this.#vars('')));
      },
      { enableHighAccuracy: false, timeout: GEO_TIMEOUT_MS, maximumAge: 10 * 60 * 1000 }
    );
  }

  /**
   * Apply a detected place: pick country/mode, prefill pincode or city, check.
   * @param {{country:string, postcode:string, city:string}} place
   * @param {boolean} auto
   */
  #applyDetected(place, auto) {
    if (place.country) this.#setCountry(place.country);
    const mode = this.mode;
    const raw = mode === 'city' ? place.city : place.postcode;
    const value = normalise(raw, mode);

    if (value && VALIDATORS[mode].test(value)) {
      this.input.value = value;
      this.check(value, { track: !auto });
      return;
    }

    // Country known but no usable pincode/city from the geocoder (keyless provider has no postcodes)
    this.input.value = '';
    this.#value = '';
    this.#render('', '');
    if (!auto) this.input.focus();
  }

  /**
   * Select a country (dropdown option when offered, else data-country) and switch mode.
   * @param {string} code
   * @returns {boolean} whether the country was applied
   */
  #setCountry(code) {
    const country = String(code || '').toUpperCase();
    if (!country) return false;
    if (this.countrySelect) {
      const option = Array.from(this.countrySelect.options).find((o) => o.value === country);
      if (!option) return false; // not a country we ship to / not offered here
      this.countrySelect.value = country;
    } else {
      this.dataset.country = country;
      delete this.dataset.countryName;
    }
    this.#applyMode(modeForCountry(country));
    this.#updateCountryNote();
    return true;
  }

  /** "Delivering to [country]" line shown when there is no country dropdown. */
  #updateCountryNote() {
    if (!this.countryNote) return;
    const text = fill(this.dataset.textCountry, { country: this.countryName });
    this.countryNote.textContent = text;
    this.countryNote.hidden = !text;
  }

  /**
   * Run a delivery check for a pincode / postcode / city.
   * @param {string} raw
   * @param {{ silent?: boolean, track?: boolean }} [options] silent = no "checking..." flash
   *   (branch re-checks); track = shopper action, send pincode_collect
   */
  async check(raw, { silent = false, track: shouldTrack = false } = {}) {
    const mode = this.mode;
    const country = this.country;
    const value = normalise(raw, mode);
    const vars = this.#vars(value);

    if (!VALIDATORS[mode].test(value)) {
      this.#value = '';
      this.#render('invalid', fill(this.#invalidText(mode), vars));
      return;
    }

    this.#value = value;
    storage.set(country, value, this.remember);
    if (shouldTrack) track('pincode_collect', { [TRACK_PARAM[mode] || 'pincode']: value, country });

    if (this.dataset.showDates !== 'true') {
      this.#abort?.abort();
      const text = country === 'IN' ? this.dataset.textDomestic : this.dataset.textInternational;
      this.#render('success', fill(text, vars));
      this.#emit({ ...vars, available: true });
      return;
    }

    // International: the ERP only knows Indian pincodes, and a foreign postcode that looks
    // like one (Volgograd 400001 = Mumbai 400001) would get an Indian answer. Never ask it.
    const intlFallback = this.intlFallback;
    if (intlFallback) {
      this.#abort?.abort();
      this.#renderResult(vars, intlFallback);
      return;
    }

    const branch = this.branch;
    const key = `${country}|${value}|${branch}`;
    const cached = cache.get(key);
    if (cached) {
      this.#renderResult(vars, cached);
      return;
    }

    if (!this.endpoint) {
      if (this.intlFallback) {
        this.#renderResult(vars, this.intlFallback);
        return;
      }
      this.#render('error', fill(this.dataset.textError, vars));
      return;
    }

    this.#abort?.abort();
    const controller = new AbortController();
    this.#abort = controller;
    const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);

    if (!silent) this.#render('loading', fill(this.dataset.textLoading, vars));

    try {
      const url = `${this.endpoint}/${encodeURIComponent(value)}/${encodeURIComponent(branch)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { apikey: this.dataset.apikey || '' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`EDD HTTP ${response.status}`);

      const data = await response.json();
      const min = Number(data?.min);
      const max = Number(data?.max);
      if (!Number.isFinite(min) || !Number.isFinite(max)) throw new Error('EDD bad payload');

      const result = { min, max };
      cache.set(key, result);
      if (this.#abort === controller) this.#renderResult(vars, result);
    } catch (error) {
      if (controller.signal.aborted && this.#abort !== controller) return; // superseded by a newer check
      console.warn('[nalli-delivery-estimate]', error);
      const fallback = this.intlFallback;
      if (fallback) this.#renderResult(vars, fallback);
      else this.#render('error', fill(this.dataset.textError, vars));
    } finally {
      window.clearTimeout(timer);
      if (this.#abort === controller) this.#abort = null;
    }
  }

  /** @param {string} value */
  #vars(value) {
    return { pincode: value, country: this.countryName };
  }

  /** @param {string} mode */
  #invalidText(mode) {
    if (mode === 'city') return this.dataset.textInvalidCity;
    if (mode === 'postcode') return this.dataset.textInvalidPostcode;
    return this.dataset.textInvalidPincode;
  }

  /** @param {string} mode */
  #placeholder(mode) {
    if (mode === 'city') return this.dataset.placeholderCity || '';
    if (mode === 'postcode') return this.dataset.placeholderPostcode || '';
    return this.dataset.placeholderPincode || '';
  }

  /**
   * Switch the input between pincode / postcode / city behaviour.
   * @param {'pincode' | 'postcode' | 'city'} mode
   */
  #applyMode(mode) {
    if (!this.input) return;
    const changed = this.dataset.mode !== mode;
    this.dataset.mode = mode;

    const attrs = INPUT_ATTRS[mode] || INPUT_ATTRS.postcode;
    for (const [attr, value] of Object.entries(attrs)) {
      if (value === null) this.input.removeAttribute(attr);
      else this.input.setAttribute(attr, value);
    }
    const placeholder = this.#placeholder(mode);
    this.input.placeholder = placeholder;
    if (this.inputLabel) this.inputLabel.textContent = placeholder;

    if (changed) {
      this.input.value = '';
      this.#value = '';
      this.#abort?.abort();
      this.#render('', '');
    }
  }

  /**
   * Prefill from the remembered { country, value } and auto-check.
   * @param {string[]} countries only restore an entry saved for one of these countries
   * @returns {boolean} true when a remembered entry was applied
   */
  #restore(countries) {
    const saved = storage.get(this.remember);
    if (!saved?.value) return false;

    const savedCountry = String(saved.country || 'IN').toUpperCase();
    if (!countries.includes(savedCountry)) return false; // e.g. an Indian pincode while shopping in USD
    if (!this.#setCountry(savedCountry)) return false; // remembered country not offered here (different market)

    const value = normalise(saved.value, this.mode);
    if (!VALIDATORS[this.mode].test(value)) return false;
    this.input.value = value;
    this.check(value);
    return true;
  }

  /**
   * @param {Record<string,string>} vars
   * @param {{min:number,max:number}} result
   */
  #renderResult(vars, result) {
    // International never comes from the ERP (and it returns 0/0 for it): use the default range.
    const intl = this.intlFallback;
    const { min, max } = intl || result;
    const sla = Math.max(min, max);
    const base = { ...vars, min: String(min), max: String(max) };

    if (sla <= 0) {
      this.#render('unavailable', fill(this.dataset.textUnavailable, base));
      this.#emit({ ...base, available: false });
      return;
    }

    // International: business days as configured. India: SLA date .. SLA + range_extra days.
    const extra = Math.max(0, Number.parseInt(this.dataset.rangeExtra ?? '', 10) || 0);
    const lo = intl ? Math.min(Math.max(min, 0), sla) : sla;
    const hi = intl ? sla : sla + extra;
    const skip = new Set(intl ? ['Sat', 'Sun'] : this.dataset.skipSundays === 'true' ? ['Sun'] : []);
    const style = this.dataset.dateStyle || 'weekday';
    const minDate = formatDate(addDays(lo, skip), style);
    const maxDate = formatDate(addDays(hi, skip), style);
    const all = {
      ...base,
      min: String(lo),
      max: String(hi),
      days: lo === hi ? String(hi) : `${lo}-${hi}`,
      date: maxDate,
      min_date: minDate,
      max_date: maxDate,
    };
    const template = lo === hi || minDate === maxDate ? this.dataset.textSingle : this.dataset.textRange;
    this.#render('success', fill(template, all));
    this.#emit({ ...all, available: true });
  }

  /**
   * @param {string} state loading | success | unavailable | invalid | error | ''
   * @param {string} text
   */
  #render(state, text) {
    if (!this.result) return;
    this.result.dataset.state = state;
    this.result.textContent = text;
    this.toggleAttribute('data-loading', state === 'loading');
    this.form?.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    this.input?.setAttribute('aria-invalid', state === 'invalid' ? 'true' : 'false');
  }

  /** @param {Record<string, unknown>} detail */
  #emit(detail) {
    document.dispatchEvent(
      new CustomEvent('nalli:edd:change', {
        bubbles: false,
        // `country` must stay the ISO code (vars carry the display name under the same key)
        detail: { ...detail, country_name: this.countryName, source: this, country: this.country, mode: this.mode, branch: this.branch },
      })
    );
  }

  #onSubmit = (event) => {
    event.preventDefault();
    this.check(this.input?.value || '', { track: true });
  };

  #onInput = () => {
    if (!this.input) return;
    if (this.mode === 'pincode') {
      const digits = this.input.value.replace(/\D/g, '').slice(0, 6);
      if (digits !== this.input.value) this.input.value = digits;
    }
    if (this.result?.dataset.state === 'invalid') this.#render('', '');
  };

  #onCountryChange = () => {
    this.#applyMode(modeForCountry(this.country));
    this.input?.focus();
  };

  #onGeoClick = (event) => {
    event.preventDefault();
    storage.flag(GEO_DENIED_KEY, false);
    this.locate({ auto: false });
  };

  /** Horizon 4.x: the variant picker dispatches this with a promise resolving to the re-rendered HTML. */
  #onProductSelect = (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (!promise?.then) return;
    promise
      .then((payload) => this.#adoptBranch(payload?.detail?.html))
      .catch(() => {
        /* aborted / superseded selection */
      });
  };

  /** Horizon 2.x: `variant:update` carries the new section HTML in detail.data.html. */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    this.#adoptBranch(detail?.data?.html || detail?.html);
  };

  /**
   * Read our own re-rendered copy out of a section-rendering response and adopt its branch.
   * @param {Document | Element | undefined} html
   */
  #adoptBranch(html) {
    if (!html?.querySelector) return;
    const fresh = html.querySelector(`nalli-delivery-estimate[data-block-id="${this.dataset.blockId}"]`);
    const branch = fresh?.getAttribute('data-branch');
    if (branch && branch !== this.dataset.branch) this.dataset.branch = branch; // -> attributeChangedCallback
  }

  /** Another instance on the page checked a location — mirror it. */
  #onSiblingChange = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail || detail.source === this || !this.input) return;
    if (detail.country === this.country && detail.pincode === this.#value) return;
    if (!this.#setCountry(detail.country)) return;
    this.input.value = detail.pincode;
    this.check(detail.pincode, { silent: true });
  };
}

if (!customElements.get('nalli-delivery-estimate')) {
  customElements.define('nalli-delivery-estimate', NalliDeliveryEstimate);
}
